// React Dependencies
import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Material UI Components
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import { TableSortLabel } from "@mui/material";

// MUI Imports
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CircleIcon from '@mui/icons-material/Circle';

// Component Imports
import { logout } from "../../../Redux/Actions/userActions";
import EditModal from "../../../Components/EditModal";
import CreateModal from "../../../Components/CreateModal"
import Loading from "../../../Components/Interactive/Loading";
import Paginate from "../../../Utilities/Paginate";
import { Alert, Stack } from "@mui/material";
import SuccessAlert from "../../../Components/Interactive/SuccessAlert";
import { formatDate } from "../../../Helpers/formatters";

const OptOutWhitelist = ({ isOpen }) => {
  const [loading, setIsLoading] = useState(false);
  const [optOutWhitelistEmails, setOptOutWhitelistEmails] = useState([]);

  const [successMessage, setSuccessMessage] = useState(undefined);
  const [editModalError, setEditModalError] = useState(undefined);
  const [createModalError, setCreateModalError] = useState(undefined);
  const clearSuccessMessages = () => setSuccessMessage(undefined);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  //#region modal
  
  const [modalIsOpen, setModalOpen] = useState(false);
  const [createModalIsOpen, setCreateModalOpen] = useState(false);

  // Event Handlers - Modals
  const handleOpen = (e) => setModalOpen(true);
  const handleClose = (e) => {
    setModalOpen(false);
    setEditModalError(undefined);
  };
  const handleOpenNew = e => setCreateModalOpen(true);
  const handleCloseNew = (e) => {
    setCreateModalOpen(false);
    setCreateModalError(undefined);
  };

  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');
  const [whitelistEmailItemToUpdate, setWhitelistEmailItemToUpdate] = useState(undefined);

  const setItemToEdit = (whitelistItem, index) => {
    setWhitelistEmailItemToUpdate({ id: whitelistItem.id, email: whitelistItem.email, index: index});
    handleOpen();
  };

  //#endregion

  //#region Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(250);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("leadDate");

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    };

    const getComparator = (order, orderBy) => {
      return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const handleRequestSort = (e, id) => {
      const isAsc = orderBy === id && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    };

    const stableSort = (array, comparator) => {
      return array
        .map((el, index) => [el, index])
        .sort((a, b) => {
          const lead = comparator(a[0], b[0]);
          if (lead !== 0) return lead;
          return a[1] - b[1];
        })
        .map((el) => el[0]);
    };
  //#endregion

  const headCells = [
    {id: "email", label: "Email"},
    {id: "createdDate", label: "Created Date"},
    {id: "active", label: "Active"},
    {id: "inactiveDate", label: "Inactive Date"},
  ];
  
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const res = await fetch(`/api/optouts/whitelists`, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        dispatch(logout());
        return navigate("/");
      }
      const result = await res.json();
      setOptOutWhitelistEmails(result.result ?? []);
      setIsLoading(false);
    })();
  }, [dispatch, navigate]);

  const userAuth = useSelector((state) => state.auth);
  const isAdmin = userAuth?.userInfo?.userData?.role === "Administrator" || userAuth?.userInfo?.userData?.role === "Super User";

  
  // Dont show the page if the user is not an admin
  if(!isAdmin)
  {
    return (<Navigate to={{pathname: "/",}}/>);
  }

  const submitWhitelistNew = async () => {

    const options = {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: newWhitelistEmail,
      }),
    };

    const res = await fetch(`/api/optouts/whitelist/create`, options);
    const parsedResult = await res.json();

    if(parsedResult.success) {
      setOptOutWhitelistEmails([parsedResult.result].concat(optOutWhitelistEmails));
      setSuccessMessage("Email successfully added!");
      setTimeout(() => clearSuccessMessages(), 5000);
      setCreateModalOpen(false);
    }
    else{
      setCreateModalError(parsedResult.error);
    }
  }

  // Update an existing whitelisted email
  const submitWhitelistUpdate = async () => {
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: whitelistEmailItemToUpdate.id,
        email: whitelistEmailItemToUpdate.email,
      }),
    };

    const res = await fetch(`/api/optouts/whitelist/update`, options);
    const parsedResponse = await res.json();

    if(parsedResponse.success) {
      setSuccessMessage("Email successfully updated!");
      console.log("updating", whitelistEmailItemToUpdate);
      var idx = optOutWhitelistEmails.findIndex(x => x.id === parsedResponse.result.id);
      optOutWhitelistEmails[idx] = parsedResponse.result;
      setTimeout(() => clearSuccessMessages(), 5000);
      setModalOpen(false);
    }
    else {
      setEditModalError(parsedResponse.error);
    }
  };

  // Delete an existing whitelisted email
  const submitWhitelistDelete = async () => {
    const options = {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: whitelistEmailItemToUpdate.id,
      }),
    };

    const res = await fetch(`/api/optouts/whitelist/delete`, options);
    const parsedResponse = await res.json();

    if(parsedResponse.success) {
      setSuccessMessage("Email successfully deleted!");
      optOutWhitelistEmails[whitelistEmailItemToUpdate.index] = parsedResponse.result;
      setTimeout(() => clearSuccessMessages(), 5000);
      setModalOpen(false);
    }
    else {
      setEditModalError(parsedResponse.error);
    }
  };

  return (
    <div className={`${isOpen ? "page" : "page-closed"} `}>
      <Stack direction="row" spacing={2}>
        <h3 className="heading">Opt Out Whitelist Emails</h3>

        <div style={{display: "flex", alignItems: "center", justifyContent:   "space-evenly", float: "right", cursor: "pointer"}}>
          <AddCircleOutlineIcon classs="edit-info-btn" onClick={handleOpenNew}/>
        </div>
      </Stack>
      
      {loading && <Loading />}

      <div className="root">
        {
          successMessage ?
          (<div style={{ position: "fixed", top: "20px", right: "20px" }}>
            <SuccessAlert className="alert-animation" >
              {successMessage}
            </SuccessAlert>
          </div>
          )
          : null
        }
        <TableContainer>
          <Table className="table" size={"small"}>
            <TableHead>
              <TableRow className="header">
                {headCells.map(({id, label}) => (
                  <TableCell
                    key={id}
                    className="headerText"
                    align="center"
                    padding="normal"
                    size="medium"
                  >
                    <TableSortLabel
                      active={true}
                      direction={orderBy === id ? order : "asc"}
                      onClick={(e) => handleRequestSort(e, id)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                    key="action"
                    className="headerText"
                    align="center"
                    padding="normal"
                    size="medium"
                  >
                  </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {optOutWhitelistEmails && optOutWhitelistEmails.length > 0 &&
               stableSort(optOutWhitelistEmails, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => {
                  return (
                    <TableRow key={index} >
                      <TableCell className="tableCell" align="center">
                        {item.active ? item.email : <strike>{item.email}</strike>}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {formatDate(item.createdDate)}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {
                          item.active ? 
                          <CircleIcon sx={{color: "green !important"}} />
                          : <CircleIcon sx={{color: "red !important"}} />
                        }
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {item.inactiveDate ? formatDate(item.inactiveDate) : "-"}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {
                          item.active &&
                          <EditIcon className="edit-info-btn" onClick={() => setItemToEdit(item, index)} style={{marginRight: "1rem"}}/>
                        }
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Paginate 
          totalRowCount={optOutWhitelistEmails.length}
          page={page} 
          rowsPerPage={rowsPerPage} 
          rowsPerPageOptions={[50, 100, 250, 500]}
          handleChange={handleChangePage} 
          handleChangeRowsPerPage={handleChangeRowsPerPage} />
      </div>

      <EditModal open={modalIsOpen} handleClose={handleClose} customCSS={{height: "auto"}}>
        <div className="modal-header">
          <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
            Edit Whitelist Email
          </h2>
          <CloseIcon
            className="close-icons"
            onClick={handleClose}
          />
        </div>
        <br/>
        <Alert severity="info">NOTE: You may only be able to update email if it's Active and NO opt-out request has come through from the current email</Alert>
        <br/>
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Email"
          name="whitelist_email_update"
          style={{ margin: 10, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={whitelistEmailItemToUpdate && whitelistEmailItemToUpdate.email}
          onChange={(e) => setWhitelistEmailItemToUpdate({ id: whitelistEmailItemToUpdate.id, email: e.target.value, index: whitelistEmailItemToUpdate.index})}
        />
        {editModalError &&
          <Alert severity="error">{editModalError}</Alert>
        }
        <br/>
        <div className="modal-btn-box">
          <button className="submit-btn" style={{marginTop: "auto"}} onClick={submitWhitelistUpdate}>
            <span>Update</span>
          </button>
          
          <button className="submit-btn" style={{marginTop: "auto"}} onClick={submitWhitelistDelete}>
            <span>Delete</span>
          </button>
        </div>
      </EditModal>
      
      <CreateModal open={createModalIsOpen} handleClose={handleCloseNew} customCSS={{height: "auto"}} >
        <div className="modal-header">
          <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
            Add Email to the Whitelist
          </h2>
          <CloseIcon
            className="close-icons"
            onClick={handleCloseNew}
          />
        </div>
        <br/>
        <Alert severity="info"><b>NOTE: You may only be able to add a new email if it does not exist or is not active</b></Alert>
        <br/>
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Email"
          name="whitelist_email_new"
          style={{ margin: 10, width: "96%" }}
          fullWidth={false}
          margin="normal"
          variant="outlined"
          value={newWhitelistEmail}
          onChange={(e) => setNewWhitelistEmail(e.target.value)}
          color="secondary"
        />
        {createModalError &&
          <Alert severity="error">{createModalError}</Alert>
        }
        <div className="modal-btn-box">
          <button className="submit-btn" onClick={submitWhitelistNew} style={{marginTop: "auto"}}>
            <span>Submit</span>
          </button>
        </div>
      </CreateModal>
    </div>
  );
};

export default OptOutWhitelist;
